/**
 * `<CalendarItemCard>` — a single row inside the month-grouped list on the
 * calendar detail page. Rendered once per `ContentCalendarItem`. Handles:
 *
 *   - Type badge (pillar / spoke / refresh).
 *   - Title (linked to the brief when one exists).
 *   - Target keyword / rationale metadata.
 *   - A6→A3 brief status pill — when `brief_id` is set, we fetch the brief
 *     and show its life-cycle stage ("Brief: draft" / "Brief: exported"
 *     depending on `brief.status` and whether it has been exported).
 *   - Generate-brief button (delegated to caller via `onGenerate`).
 *   - Scheduled-for date.
 *
 * Extracted from the calendar detail route so the item card can be reused
 * in month drill-downs, kanban swaps, etc.
 */
import { Link } from '@tanstack/react-router';
import { FileText, Loader2 } from 'lucide-react';
import type { ContentCalendarItem } from '@seo/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useBrief } from '@/features/briefs/use-brief';

/**
 * Map a brief's server-side status to a short badge label. The three copy
 * strings mirror what the plan doc calls for ("Brief: draft" / "Brief:
 * exported"). Anything terminal-with-content maps to "exported"; anything
 * else in-flight maps to "draft". Failed briefs surface separately so the
 * user isn't led to think the linkage is healthy.
 */
function briefBadgeLabel(status: string, deliveredAt: string | null): string | null {
  if (status === 'failed' || status === 'cancelled') return 'Brief: failed';
  if (status === 'completed') return deliveredAt ? 'Brief: exported' : 'Brief: ready';
  // queued / researching / drafting / delivering all fold to "draft" for the
  // calendar surface — the brief page itself shows the granular state.
  return 'Brief: draft';
}

interface CalendarItemCardProps {
  item: ContentCalendarItem;
  onGenerate?: (itemId: string) => void;
  /** True when a brief-generate is in flight for this exact item. */
  isGenerating?: boolean;
  /** True when any generate is in flight (disable other rows' buttons). */
  isAnyGenerating?: boolean;
}

export function CalendarItemCard({
  item,
  onGenerate,
  isGenerating = false,
  isAnyGenerating = false,
}: CalendarItemCardProps): JSX.Element {
  // Only fetch the brief when the item has one — avoids a wasted request per
  // planned-but-unbriefed row.
  const briefQ = useBrief(item.brief_id ?? undefined);
  const brief = briefQ.data;
  const briefLabel = brief
    ? briefBadgeLabel(brief.status, brief.delivered_at)
    : null;

  return (
    <li className="flex items-start justify-between gap-3 rounded-md border border-border/60 bg-background/40 p-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={typeBadge(item.type)}>{item.type}</Badge>
          {item.brief_id ? (
            <Link
              to="/briefs/$briefId"
              params={{ briefId: item.brief_id }}
              className="font-medium text-foreground hover:underline"
            >
              {item.title}
            </Link>
          ) : (
            <div className="font-medium text-foreground">{item.title}</div>
          )}
          {/* Only show the brief-status badge when we have a linked brief AND
              a resolved status. Loading the brief is silent — the row still
              shows "Brief linked" via the right-hand pill below. */}
          {item.brief_id && briefLabel ? (
            <Badge
              variant="outline"
              className="text-[11px] font-normal text-muted-foreground"
            >
              {briefLabel}
            </Badge>
          ) : null}
        </div>
        {item.target_keyword ? (
          <div className="mt-0.5 text-xs text-muted-foreground">
            Target keyword: <span className="font-mono">{item.target_keyword}</span>
          </div>
        ) : null}
        {item.rationale ? (
          <p className="mt-1 text-xs text-muted-foreground/90">{item.rationale}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
        {item.brief_id ? (
          <Badge
            variant="outline"
            className="text-[11px] font-normal text-muted-foreground"
          >
            <FileText className="mr-1 h-3 w-3" /> Brief linked
          </Badge>
        ) : item.target_keyword && onGenerate ? (
          <Button
            variant="outline"
            size="sm"
            disabled={isGenerating || isAnyGenerating}
            onClick={() => onGenerate(item.id)}
          >
            {isGenerating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileText className="h-3.5 w-3.5" />
            )}
            {isGenerating ? 'Generating…' : 'Generate brief'}
          </Button>
        ) : null}
        <span className="text-xs text-muted-foreground">
          {new Date(item.scheduled_for).toLocaleDateString()}
        </span>
      </div>
    </li>
  );
}

function typeBadge(t: string): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (t === 'pillar') return 'default';
  if (t === 'spoke') return 'secondary';
  return 'outline';
}
